// Offline read-through cache for the clock-in/clock-out data flow.
//
// CommonAPIService.getCall consults this module: successful responses to the
// allowlisted endpoints below are stored in IndexedDB; when the same request
// later fails with a network error (offline), the cached copy is served so
// the clock modals can still render. Only the endpoints the clock flow needs
// are cached — this is not a general HTTP cache.
//
// PHI note: cached medication/goal/shift data is PHI at rest in the browser.
// It is cleared on logout (authService calls clearClockDataCache) and
// entries expire after MAX_AGE_MS.

import { dbGet, dbPut, dbGetAll, dbClear } from "./offlineDb";

const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // stale entries are never served after 7 days

// category feeds the sync-status UI ("what do I have offline?").
const CACHEABLE = [
  { re: /^\/api\/shifts\/current/, category: "Your shifts" },
  { re: /^\/api\/shifts\/by-role/, category: "Your shifts" },
  { re: /^\/api\/clients\/\d+\/medications/, category: "Client medications" },
  { re: /^\/api\/clients\/\d+\/goals/, category: "Client goals" },
  { re: /^\/api\/shift-note-questions\/by-service-code\//, category: "Shift note questions" },
  { re: /^\/api\/vr_activities/, category: "Activity lists" },
  { re: /^\/api\/service_codes/, category: "Service codes" },
  { re: /^\/api\/non_service_codes/, category: "Service codes" },
  { re: /^\/api\/job_coaching_activities/, category: "Activity lists" },
  { re: /^\/api\/caregiverCompliance\/check-clock-in\//, category: "Compliance checks" },
  { re: /^\/api\/documentAcknowledgments\/clock-in-check\//, category: "Compliance checks" },
];

function match(url) {
  return CACHEABLE.find((c) => c.re.test(url)) || null;
}

export function isCacheable(url) {
  return !!match(url);
}

function cacheKey(url, params) {
  const paramKeys = Object.keys(params || {}).sort();
  const qs = paramKeys.map((k) => `${k}=${params[k]}`).join("&");
  return qs ? `${url}?${qs}` : url;
}

export function storeResponse(url, params, data) {
  const entry = match(url);
  if (!entry) return Promise.resolve();
  return dbPut("cache", {
    key: cacheKey(url, params),
    category: entry.category,
    cachedAt: new Date().toISOString(),
    data: JSON.parse(JSON.stringify(data)),
  }).catch(() => {
    /* cache is best-effort */
  });
}

/**
 * Cached copy for a request, or null. The returned data carries
 * non-enumerable __fromCache / __cachedAt markers so callers can tell the
 * user they're looking at saved data.
 */
export async function getCachedResponse(url, params) {
  try {
    const row = await dbGet("cache", cacheKey(url, params));
    if (!row) return null;
    if (Date.now() - new Date(row.cachedAt).getTime() > MAX_AGE_MS) return null;
    const data = row.data;
    if (data && typeof data === "object") {
      try {
        Object.defineProperty(data, "__fromCache", { value: true });
        Object.defineProperty(data, "__cachedAt", { value: row.cachedAt });
      } catch {
        /* frozen — still usable */
      }
    }
    return data;
  } catch {
    return null;
  }
}

/**
 * Summary for the sync-status UI: [{category, count, newest}] of everything
 * saved for offline use.
 */
export async function getCacheSummary() {
  try {
    const rows = await dbGetAll("cache");
    const byCategory = {};
    for (const row of rows) {
      if (Date.now() - new Date(row.cachedAt).getTime() > MAX_AGE_MS) continue;
      const c = (byCategory[row.category] = byCategory[row.category] || {
        category: row.category,
        count: 0,
        newest: null,
      });
      c.count += 1;
      if (!c.newest || row.cachedAt > c.newest) c.newest = row.cachedAt;
    }
    return Object.values(byCategory).sort((a, b) =>
      a.category.localeCompare(b.category),
    );
  } catch {
    return [];
  }
}

/** Wipe cached offline data (logout / user switch). */
export function clearClockDataCache() {
  return dbClear("cache").catch(() => {
    /* ignore */
  });
}
